import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Box, Typography, Switch, FormControlLabel } from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AppButton from '../../../common_components/AppButton';
import { Dropdown, TextInput } from '../../../common_components/FormInputs';
import useToast from '../../../common_components/Toast/useToast';
import CommonDrawerLayout from '../../../common_components/CommonDrawerLayout';
import ConfirmActionModal from '../../../common_components/ConfirmActionModal';
import { bulkUpdateClientIcpFields, fetchDropdownOptions, selectAffiliateNetworkOptions, } from '../../../redux/activeClientListSlice';
import { useDispatch, useSelector } from 'react-redux';
import { AFFILIATE_NETWORKS } from '../../../utils/networkConstants';
const AFFILIATE_NETWORK_FALLBACK = AFFILIATE_NETWORKS.map(n => ({ value: n.name, label: n.label }));
const toNetworkValue = (raw) => raw.trim().toLowerCase().replace(/\s+/g, '_');
const buildNetworkRows = (client) => {
    const blankRow = {
        id: 1, sourceId: null, networkName: '', affiliateNetwork: '', programId: '', signupLink: '',
    };
    if (!client?.icp)
        return [blankRow];
    const icp = client.icp;
    const rows = [];
    const multiNetworks = Array.isArray(icp.multiple_networks) ? icp.multiple_networks : [];
    const hasMain = !!(icp.affiliate_network || icp.program_id || icp.sign_up_link);
    if (hasMain) {
        const affNet = icp.affiliate_network ? String(icp.affiliate_network).trim() : '';
        rows.push({
            id: 1,
            sourceId: null,
            networkName: icp.affiliate_network_name || affNet,
            affiliateNetwork: toNetworkValue(affNet),
            programId: icp.program_id || '',
            signupLink: icp.sign_up_link || '',
        });
    }
    multiNetworks.forEach((m) => {
        const affNet = m.affiliate_network ? String(m.affiliate_network).trim() : '';
        const name = m.name ? String(m.name).trim() : '';
        rows.push({
            id: rows.length + 1,
            sourceId: m?.sub_item_id ?? null,
            networkName: name || affNet,
            affiliateNetwork: toNetworkValue(affNet),
            programId: m.program_id || '',
            signupLink: m.sign_up_link || '',
        });
    });
    return rows.length > 0 ? rows : [blankRow];
};
const getInitialMainRowId = (client) => {
    if (!client?.icp)
        return null;
    const icp = client.icp;
    const hasMain = !!(icp.affiliate_network || icp.program_id || icp.sign_up_link);
    return hasMain ? 1 : null;
};
const AddNetworkDrawer = ({ open, onClose, client, onSuccess, canManage = true }) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const reduxNetworkOptions = useSelector(selectAffiliateNetworkOptions);
    const networkOptions = reduxNetworkOptions.length > 0 ? reduxNetworkOptions : AFFILIATE_NETWORK_FALLBACK;
    const [rows, setRows] = useState(() => buildNetworkRows(client));
    const [mainRowId, setMainRowId] = useState(() => getInitialMainRowId(client));
    const [saving, setSaving] = useState(false);
    const [rowErrors, setRowErrors] = useState({});
    const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
    useEffect(() => {
        if (open) {
            setRows(buildNetworkRows(client));
            setMainRowId(getInitialMainRowId(client));
            setRowErrors({});
            dispatch(fetchDropdownOptions('affiliate-networks'));
        }
    }, [open, client, dispatch]);
    const updateRow = (id, field, value) => {
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
        if (field === 'affiliateNetwork' || field === 'programId') {
            setRowErrors((prev) => ({
                ...prev,
                [id]: { ...prev[id], [field]: value.trim() === '' ? prev[id]?.[field] : false },
            }));
        }
    };
    const handleRemoveRow = () => {
        if (deleteModal.id === null)
            return;
        const id = deleteModal.id;
        setRows((prev) => {
            const next = prev.filter((r) => r.id !== id);
            if (next.length === 0 || id === mainRowId) {
                setMainRowId(null);
            }
            return next;
        });
        setDeleteModal({ open: false, id: null });
    };
    const handleMainToggle = (id) => {
        setMainRowId((prev) => (prev === id ? null : id));
    };
    const addRow = () => setRows((prev) => {
        const newId = prev.length ? Math.max(...prev.map((r) => r.id)) + 1 : 1;
        return [
            ...prev,
            { id: newId, sourceId: null, networkName: '', affiliateNetwork: '', programId: '', signupLink: '' },
        ];
    });
    const handleSave = async () => {
        if (!client?._id)
            return;
        // Validate required fields: Affiliate Network + Program ID for all rows
        const errors = {};
        rows.forEach((r) => {
            const missingNetwork = !r.affiliateNetwork || r.affiliateNetwork.trim() === '';
            const missingProgram = !r.programId || r.programId.trim() === '';
            if (missingNetwork || missingProgram) {
                errors[r.id] = {
                    affiliateNetwork: missingNetwork,
                    programId: missingProgram,
                };
            }
        });
        if (Object.keys(errors).length > 0) {
            setRowErrors(errors);
            return;
        }
        setRowErrors({});
        const toLabel = (val) => networkOptions.find((o) => o.value === val)?.label ?? val;
        const mainRow = mainRowId != null ? rows.find((r) => r.id === mainRowId) ?? null : null;
        const mainAffiliateNetwork = mainRow
            ? (mainRow.affiliateNetwork ? toLabel(mainRow.affiliateNetwork) : null) || null
            : null;
        const multipleNetworksPayload = rows
            .filter((r) => r.id !== mainRowId)
            .map((r) => ({
            sub_item_id: r.sourceId ?? null,
            name: (r.networkName || '').trim() || null,
            affiliate_network: r.affiliateNetwork ? toLabel(r.affiliateNetwork) : null,
            program_id: (r.programId || '').trim() || null,
            sign_up_link: (r.signupLink || '').trim() || null,
        }));
        setSaving(true);
        try {
            const fields = {
                affiliate_network: mainAffiliateNetwork,
                affiliate_network_name: mainRow ? (mainRow.networkName?.trim() || null) : null,
                program_id: mainRow ? (mainRow.programId?.trim() || null) : null,
                sign_up_link: mainRow ? (mainRow.signupLink?.trim() || null) : null,
                multiple_networks: multipleNetworksPayload,
            };
            await dispatch(bulkUpdateClientIcpFields({ updates: [{ clientId: client._id, fields }] })).unwrap();
            onSuccess?.();
            onClose();
        }
        finally {
            setSaving(false);
        }
    };
    return (_jsxs(CommonDrawerLayout, { open: open, onClose: onClose, title: "Add or Remove Affiliate Network", isLoading: saving, width: "md", headerActions: canManage ? (_jsx(AppButton, { variant: 'contained', size: 'small', onClick: handleSave, disabled: saving, startIcon: _jsx(SaveOutlinedIcon, { sx: { fontSize: 18 } }), sx: { textTransform: 'none', borderRadius: '999px', px: 2.5, fontSize: 13, fontWeight: 600, bgcolor: '#FF008E', '&:hover': { bgcolor: '#e0007c' } }, children: "Save" })) : undefined, children: [_jsxs(Box, { sx: { px: 1, py: 1, display: 'flex', flexDirection: 'column', gap: 1 }, children: [rows.map((row, index) => (_jsxs(Box, { sx: { borderRadius: 1, border: '1px solid #E5E7EB', p: 1, display: 'flex', flexDirection: 'column', gap: 1, bgcolor: '#FFFFFF' }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' }, children: [_jsxs(Typography, { sx: { fontSize: 12, fontWeight: 600, color: '#FF008E' }, children: ["Network #", index + 1] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5 }, children: [canManage && (_jsx("div", { className: "compare-toggle-container normal-switch", children: _jsx(FormControlLabel, { className: "compare-label", control: _jsx(Switch, { className: "compare-switch", size: "medium", checked: row.id === mainRowId, onChange: () => handleMainToggle(row.id), sx: {
                                                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#ff008e' },
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#ff008e' },
                                                        }, inputProps: { 'aria-label': 'Main Network toggle' } }), label: _jsx(Typography, { sx: { fontSize: 11, color: '#4B5563', fontWeight: 500 }, children: "Main Network" }), sx: { m: 0 } }) })), canManage && (_jsx("img", { src: "/assets/delete.svg", style: { border: '1px solid #FEE2E2', borderRadius: '8px', padding: "4px", cursor: "pointer" }, alt: "delete", onClick: () => setDeleteModal({ open: true, id: row.id }) }))] })] }), _jsxs(Box, { sx: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }, children: [_jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: 11, color: '#667085', mb: 0.5, fontWeight: 500 }, children: "Network Name" }), _jsx(TextInput, { label: undefined, size: 'sm', fullWidth: true, placeholder: 'Enter network name', value: row.networkName, onChange: (e) => updateRow(row.id, 'networkName', e.target.value), disabled: !canManage })] }), _jsxs(Box, { children: [_jsxs(Typography, { sx: { fontSize: 11, color: '#667085', mb: 0.5, fontWeight: 500 }, children: ["Affiliate Network", _jsx(Box, { component: 'span', sx: { color: '#EF4444', ml: 0.5 }, children: "*" })] }), _jsx(Dropdown, { value: row.affiliateNetwork, onChange: (val) => updateRow(row.id, 'affiliateNetwork', Array.isArray(val) ? val[0] ?? '' : String(val ?? '')), options: networkOptions, size: 'sm', placeholder: 'Select network', fullWidth: true, error: !!rowErrors[row.id]?.affiliateNetwork, errorMessage: rowErrors[row.id]?.affiliateNetwork ? 'Affiliate Network is required' : undefined, disabled: !canManage })] }), _jsxs(Box, { children: [_jsxs(Typography, { sx: { fontSize: 11, color: '#667085', mb: 0.5, fontWeight: 500 }, children: ["Program ID", _jsx(Box, { component: 'span', sx: { color: '#EF4444', ml: 0.5 }, children: "*" })] }), _jsx(TextInput, { label: undefined, size: 'sm', fullWidth: true, placeholder: 'Enter program id', value: row.programId, onChange: (e) => updateRow(row.id, 'programId', e.target.value), error: !!rowErrors[row.id]?.programId, errorMessage: rowErrors[row.id]?.programId ? 'Program ID is required' : undefined, disabled: !canManage })] }), _jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: 11, color: '#667085', mb: 0.5, fontWeight: 500 }, children: "Sign up Link" }), _jsx(TextInput, { label: undefined, size: 'sm', fullWidth: true, placeholder: 'Enter signup link', value: row.signupLink, onChange: (e) => updateRow(row.id, 'signupLink', e.target.value), disabled: !canManage })] })] })] }, row.id))), canManage && (_jsx(Box, { sx: { mt: 1, textAlign: 'end' }, children: _jsxs(AppButton, { variant: 'outlined', size: 'small', onClick: addRow, sx: { textTransform: 'none', px: 2, borderColor: '#D0D5DD', color: '#344054', bgcolor: '#FFFFFF', '&:hover': { bgcolor: '#F9FAFB' } }, children: [_jsx(Typography, { component: 'span', sx: { fontSize: 18, marginRight: 1 }, children: "+" }), " Add New Network"] }) }))] }), _jsx(ConfirmActionModal, { open: deleteModal.open, title: "Remove Network", description: "Are you sure you want to remove this network? This action will remove the network from the list.", onConfirm: handleRemoveRow, onClose: () => setDeleteModal({ open: false, id: null }), confirmText: "Remove", isDeleteIcon: true })] }));
};
export default AddNetworkDrawer;
